import { useEffect, useState } from 'react';
import {
  Appointment,
  ApiError,
  RestoreResult,
  TimeOff,
  deleteAdminTimeOff,
  fetchAdminTimeOffRestorable,
} from '../api';
import { formatSalon } from '../lib/datetime';
import { AlertCircle, Loader2, Trash2, X } from 'lucide-react';

export interface DeleteOutcome {
  /** False when the owner backed out — the block is still there. */
  deleted: boolean;
  /** True when at least one booking came back, so the calendar needs rereading. */
  restoredAny: boolean;
}

interface TimeOffDeleteDialogProps {
  open: boolean;
  target: TimeOff | null;
  /** The block in words — days and hours — as the list renders it. */
  description: string;
  onClose: (outcome: DeleteOutcome) => void;
}

/**
 * Confirms deleting a block, and asks about its cancelled bookings in the same
 * breath.
 *
 * The bookings are fetched *before* anything is deleted, while the block still
 * stands. That ordering is the point. The times it covers stay closed to the
 * public for as long as this dialog is open, so nobody can take a slot out from
 * under a booking that is about to be offered back — and the link marking these
 * as the salon's own cancellations, rather than the customer's, is still intact
 * when the answer is applied. Deleting first and asking afterwards loses both.
 *
 * Bringing them back is the default. The customer was never told they were
 * cancelled, so a booking left cancelled is one somebody still expects to keep.
 */
export default function TimeOffDeleteDialog({
  open,
  target,
  description,
  onClose,
}: TimeOffDeleteDialogProps) {
  const [loading, setLoading] = useState(true);
  const [restorable, setRestorable] = useState<Appointment[]>([]);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [restore, setRestore] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<RestoreResult | null>(null);

  const id = target?.id;

  useEffect(() => {
    if (!open || id === undefined) return;
    let stale = false;
    setLoading(true);
    setRestorable([]);
    setPreviewFailed(false);
    setRestore(true);
    setError('');
    setResult(null);
    fetchAdminTimeOffRestorable(id)
      .then((block) => {
        if (!stale) setRestorable(block.restorable ?? []);
      })
      .catch(() => {
        // Not fatal: the owner can still delete, they just do it blind. But
        // blind must not mean *destructive* — an empty list here is "the read
        // failed", not "there is nothing to bring back", and deleting without
        // asking for the restore severs cancelled_by_time_off for good (it is
        // SET_NULL), after which restore_blocked_reason answers "not cancelled
        // by time off" forever. So the delete still asks; the backend recomputes
        // the list from the block itself and reports whatever it had to skip.
        if (!stale) {
          setPreviewFailed(true);
          setError(
            "Couldn't check which bookings this would bring back. Deleting will still try to " +
              'bring them back, and will tell you about any it could not.',
          );
        }
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [open, id]);

  if (!open || !target) return null;

  const count = restorable.length;
  // `previewFailed` counts as "maybe some" — see the catch above. Asking for a
  // restore that turns out to be empty costs nothing; skipping one that wasn't
  // is unrecoverable.
  const bringBack = restore && (count > 0 || previewFailed);

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      const res = await deleteAdminTimeOff(target.id, bringBack);
      // Anything that couldn't come back is reported before the dialog closes —
      // the owner has to phone those customers, so they can't be dropped
      // silently.
      if (res.skipped.length > 0) {
        setResult(res);
        setDeleting(false);
        return;
      }
      onClose({ deleted: true, restoredAny: res.restored.length > 0 });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete this time off.');
    }
    setDeleting(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="animate-in fade-in zoom-in bg-surface flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl shadow-xl duration-200">
        <div className="border-subtle flex items-start justify-between gap-3 border-b px-6 py-4">
          <div className="flex gap-3">
            <span className="bg-danger-soft mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
              <Trash2 className="text-danger h-4 w-4" />
            </span>
            <div>
              <h3 className="text-fg text-lg leading-tight font-bold">
                {result ? 'Time off deleted' : 'Delete this time off?'}
              </h3>
              <p className="text-fg-muted mt-0.5 text-sm">
                {target.staffName ?? 'The whole salon'} — {description}
              </p>
            </div>
          </div>
          {!result && (
            <button
              onClick={() => onClose({ deleted: false, restoredAny: false })}
              disabled={deleting}
              className="hover:bg-muted -mt-1 rounded-full p-2 transition-colors disabled:pointer-events-none disabled:opacity-40"
            >
              <X className="text-fg-subtle h-5 w-5" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {result ? (
            <div>
              <p className="text-fg-secondary text-sm">
                {result.restored.length > 0
                  ? `${result.restored.length} booking${result.restored.length === 1 ? '' : 's'} came back, confirmed.`
                  : 'Those times are bookable again.'}
              </p>
              <p className="text-warn mt-4 text-sm font-semibold">
                Couldn't bring {result.skipped.length === 1 ? 'this one' : 'these'} back:
              </p>
              <ul className="mt-2 space-y-1">
                {result.skipped.map((s) => (
                  <li key={s.id} className="text-warn flex items-center gap-2 text-sm">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    <span className="font-medium">{s.name}</span>
                    <span>{formatSalon(s.startTime, 'EEE d MMM · h:mm a')}</span>
                    <span className="ml-auto shrink-0 text-xs">{s.reason}</span>
                  </li>
                ))}
              </ul>
              <p className="text-fg-muted mt-3 text-xs">
                They're still cancelled, and they were never told — so they're yours to call.
              </p>
            </div>
          ) : (
            <>
              <p className="text-fg-secondary text-sm">
                Those times become bookable again straight away.
              </p>

              {loading ? (
                <div className="text-fg-muted mt-4 flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking for bookings you cancelled for it…
                </div>
              ) : (
                count > 0 && (
                  <div className="mt-4">
                    <p className="text-fg text-sm font-semibold">
                      You cancelled {count} booking{count === 1 ? '' : 's'} for this.
                    </p>
                    <ul className="divide-subtle border-default mt-2 divide-y rounded-lg border">
                      {restorable.map((appointment) => (
                        <li key={appointment.id} className="px-3 py-2.5">
                          <p className="text-fg text-sm font-semibold">
                            {formatSalon(appointment.startTime, 'EEE d MMM')} ·{' '}
                            {formatSalon(appointment.startTime, 'h:mm a')}
                          </p>
                          <p className="text-fg-secondary text-xs">
                            {appointment.serviceName} — {appointment.customer.name}
                            {appointment.staffName ? ` · ${appointment.staffName}` : ''}
                          </p>
                        </li>
                      ))}
                    </ul>
                    <label className="border-default mt-3 flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={restore}
                        disabled={deleting}
                        onChange={(e) => setRestore(e.target.checked)}
                        className="accent-brand-600 mt-0.5 h-4 w-4 shrink-0"
                      />
                      <span className="text-fg-secondary text-sm">
                        Bring {count === 1 ? 'it' : 'them'} back, confirmed. The customer was never
                        told it was cancelled, so there's nothing to correct — but any slot taken in
                        the meantime can't be given back.
                      </span>
                    </label>
                  </div>
                )
              )}

              {error && (
                <div className="border-danger/25 bg-danger-soft text-danger mt-3 rounded-lg border px-3 py-2 text-sm">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-subtle flex gap-3 border-t px-6 py-4">
          {result ? (
            <button
              onClick={() => onClose({ deleted: true, restoredAny: result.restored.length > 0 })}
              className="bg-brand-600 hover:bg-brand-700 flex-1 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={() => onClose({ deleted: false, restoredAny: false })}
                disabled={deleting}
                className="border-default text-fg-secondary hover:bg-canvas flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50"
              >
                Keep it
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting || loading}
                className="bg-danger-solid hover:bg-danger-solid-hover flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                {bringBack && count > 0 ? `Delete and restore ${count}` : 'Delete'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
